import Spec from "csi/core/Spec";
import { C8AttributeKey } from "csi/typings";
import { isMetaOrCtrlKey, isShiftKey } from "csi/utility/eventUtility";
import { DndItemWithId } from "designSystem/dnd/index";
import { action, makeObservable, observable } from "mobx";
import { MouseEvent, SyntheticEvent } from "react";
import { twoAttributesHaveMatchingGroupAndContainer } from "./attributeService";
import CustomViewAttributeContainer from "./CustomViewAttributeContainer";
import CustomViewAttributeGroup from "./CustomViewAttributeGroup";
import CustomViewAttributesStore from "./CustomViewAttributesStore";

class CustomViewAttribute implements DndItemWithId {
	/** container for attribute */
	readonly container: CustomViewAttributeContainer;

	/** indicates if it is hidden */
	@observable hidden = false;

	/** parent group for attribute */
	@observable.ref parentGroup: CustomViewAttributeGroup;

	/** the next time this attribute is rendered, scroll it into view */
	@observable scheduledForScrollIntoView = false;

	/** indicates if it is selected */
	@observable selected = false;

	/** spec for attribute */
	readonly spec: Spec;

	/** array of attributes */
	get attributes(): CustomViewAttribute[] {
		return [this];
	}

	/** indicates if attribute can be added to the view */
	get canAddToView(): boolean {
		const { container, selected } = this;
		return !selected && container.store.canAddNumberOfAttributes(1);
	}

	/** indicates if attribute is in a named group */
	get grouped(): boolean {
		return !this.parentGroup.selected && !!this.parentGroup.name;
	}

	/** unique id for attribute */
	get id(): C8AttributeKey {
		return this.spec.uniqueId;
	}

	/** unique key with this attribute id and its parent group id */
	get idWithParent(): string {
		return `${this.spec.uniqueId}_${this.parentGroup.uniqueId}`;
	}

	/** get whether this attribute is highlighted */
	get isHighlighted(): boolean {
		return this.store.attributeDndStore.isItemSelected(this);
	}

	/** global id per store, used for dnd multi select */
	get globalId(): string {
		return `${this.idWithParent}_${this.parentGroup.selected}`;
	}

	/** order of attribute in list */
	get order(): number {
		return this.parentGroup.attributes.indexOf(this);
	}

	/** create idWithParent with external parent group */
	createIdWithParent = (group: CustomViewAttributeGroup): string => {
		return `${this.id}_${group.uniqueId}`;
	};

	/** attribute store */
	get store(): CustomViewAttributesStore {
		return this.container.store;
	}

	/** return copy of attribute except for parent group */
	copy = (): CustomViewAttribute => {
		return new CustomViewAttribute(this.container, this.spec);
	};

	/** does the attribute exist in selections */
	get existsInSelections(): boolean {
		return this.store.selectionSet.has(this.id);
	};
	
	/** does the attribute exist in selections and is the parent an attribute group */
	get existsInSelectionsAndParentIsNotAttributeGroup(): boolean {
		return this.existsInSelections && (!this.store.supportsGrouping || !this.parentGroup.isAttributeGroup);
	}

	/** does the attribute exist in unique selections by considering the parent group id  */
	get existsInUniqueSelections(): boolean {
		return this.store.selectionSetWithParent.has(this.idWithParent);
	};

	/** is there a matching matrix group for an attribute */
	get matchingMatrixGroup(): CustomViewAttributeGroup | undefined {
		const uniqueId = this.container.matrixGroupSpec?.uniqueId;
		return this.store.selections.find((selection) => selection.spec?.uniqueId === uniqueId);
	};

	/** key to render the attribute in a list */
	get renderKey(): string {
		return this.id + this.selected + this.canAddToView;
	}

	/** reevaluate the selection based on id, parent, and selection keys */
	@action reevaluateSelection = (): void => {
		this.selected = this.existsInUniqueSelections || this.existsInSelectionsAndParentIsNotAttributeGroup;
	};

	/** reset scroll into view flag */
	@action resetScrollIntoViewFlag = (): void => {
		this.scheduledForScrollIntoView = false;
	};

	/** add attribute to, or remove attribute from, selection list */
	@action onClickAvailableAttribute = (): void => {
		const { selected, store: { addAvailableAttribute, removeAvailableAttribute } } = this;

		if (selected) {
			removeAvailableAttribute(this);
		} else {
			addAvailableAttribute(this);
		}
	};

	/** add attribute to selection list */
	@action onClickAddAttribute = (e: SyntheticEvent<HTMLButtonElement>): void => {
		e.stopPropagation();
		this.store.addAvailableAttribute(this);
	};

	/** remove attribute from selection list */
	@action onClickRemoveAttribute = (e: SyntheticEvent<HTMLButtonElement>): void => {
		e.stopPropagation();
		this.store.removeAvailableAttribute(this);
	};

	/** on click handler for list item */
	@action onClickListItem = (event: MouseEvent<HTMLElement>): void => {
		this.onClickAttribute(event);
	};

	/** on click handler for selected attribute */
	@action onClickSelectedAttribute = (event: MouseEvent<HTMLFieldSetElement>): void => {
		this.onClickAttribute(event, true);
	};

	/** on click handler for attribute */
	@action onClickAttribute = (event: MouseEvent<HTMLElement>, isSelected?: boolean): void => {
		event.stopPropagation();
		const { store } = this;
		const { attributeDndStore, shiftSelectItems } = store;
		const {
			deselectAllItems,
			lastSelectedItem,
			multiSelectMap,
			selectItem,
		} = attributeDndStore;
		const canDrag = isSelected || this.canAddToView;

		// get what key(s) were pressed
		const isMetaOrCtrl = isMetaOrCtrlKey(event);
		const isShift = isShiftKey(event);

		// check if shift key was pressed and if a selection could be made
		let isMultiSelectionValid = false;
		if (isShift && !isMetaOrCtrl) {
			isMultiSelectionValid = shiftSelectItems(this, lastSelectedItem, multiSelectMap, isSelected);
		}

		if (isMetaOrCtrl && !isShift) {
			isMultiSelectionValid = !lastSelectedItem || twoAttributesHaveMatchingGroupAndContainer(lastSelectedItem, this);
		}

		// deselect other items if multi-select key was not pressed or if shift key was pressed and no selection could be made
		if (!isMultiSelectionValid || !isMetaOrCtrl && !isShift) {
			deselectAllItems();
		}

		// if no selection could be made, and the user is allowed to drag on a multi-select item, then select the item
		if ((!isMultiSelectionValid || isMetaOrCtrl) && canDrag) {
			selectItem(this);
		}
	};

	/** toggle hidden status when clicked */
	@action onClickHidden = (): void => {
		this.hidden = !this.hidden;
	};

	/** remove attribute as a selection */
	@action onRemoveSelectedAttribute = (e: MouseEvent<HTMLButtonElement>): void => {
		// C8-72530 don't select the item by propagating the event to the parent
		e.stopPropagation();
		// also, before removing the item, make sure it is not selected
		const { store } = this;
		store.attributeDndStore.removeSelectedAttribute(this);
		store.removeSelectedAttribute(this);
	};	

	constructor(container: CustomViewAttributeContainer, spec: Spec, selected?: boolean) {
		makeObservable(this);
		this.container = container;
		this.selected = !!selected;
		this.spec = spec;
	}
}

export default CustomViewAttribute;
